import { useMemo } from 'react';

import { cn, iaRa } from 'lib/helpers/utility';

import DocText from 'components/shared/DocumentSheet/DocText';
import { DOC_TEXT } from 'components/shared/DocumentSheet/lib/docStyles';
import { getInvoicesFromSelectedRows } from 'components/practice/BeyondBilling/ElectronicInvoicing/lib/groupInvoices';
import InvoiceDocument from 'components/practice/BeyondBilling/ElectronicInvoicing/components/Invoice/components/InvoiceDocument/InvoiceDocument';

import { INFORMATION_ITEMS } from './lib/constants';
import PaidStatus from './components/InvoiceDetails/PaidStatus';

// The document is typeset for print at 13px body; the patient-facing page reads
// at the app's own body size, so the whole sheet scales by that ratio.
const TEXT_ZOOM = 14 / 13;

// Set in the footer's own type, so the notices and the copyright line read as
// one block of fine print rather than three competing styles.
const footerNotes = INFORMATION_ITEMS.map((item) => (
  <DocText key={item.text} size="xs" className={DOC_TEXT.footer}>
    {item.text}{' '}
    <a
      href={item.href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary-500 underline">
      {item.label}
    </a>
  </DocText>
));

/**
 * The patient-facing invoice renders the same document as the emailed PDF, so a
 * patient comparing the two reads one layout. The internal invoices behind a
 * token merge into a single document exactly as the PDF merges them.
 */
const InvoiceWithTokenContent = ({
  invoice,
  invoices,
  amountDue,
  surcharge,
  billedToAction,
  className
}) => {
  // Display Options live on the external invoice, so the merged documents
  // inherit them rather than whatever the internal rows happened to carry.
  const documents = useMemo(
    () =>
      iaRa(getInvoicesFromSelectedRows(iaRa(invoices))).map((merged) => ({
        ...merged,
        hidden_fields: invoice?.hidden_fields ?? merged?.hidden_fields,
        aeob: invoice?.aeob ?? merged?.aeob
      })),
    [invoices, invoice?.hidden_fields, invoice?.aeob]
  );

  return (
    <div
      className={cn('flex flex-1 flex-col overflow-y-auto', amountDue > 0 && 'mb-20', className)}>
      {invoice?.paid && (
        <div className="flex justify-end px-6 pt-5 sm:px-2">
          <PaidStatus />
        </div>
      )}

      <div className="flex w-full flex-1 flex-col gap-6">
        {documents.map((document) => (
          <InvoiceDocument
            key={document.id}
            invoice={document}
            externalInvoice={invoice}
            practice={invoice?.practice}
            modules={invoice?.practice?.modules}
            withAging={false}
            surcharge={surcharge}
            fluid
            fullWidth
            fillHeight={documents.length === 1}
            zoom={TEXT_ZOOM}
            footerNotes={footerNotes}
            billedToAction={billedToAction}
          />
        ))}
      </div>
    </div>
  );
};

export default InvoiceWithTokenContent;
